import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Gift, Copy, Check, Crown, MousePointerClick, UserPlus, CreditCard, Clock,
  Wallet, Loader2, Link2, DollarSign, FileText, Image as ImageIcon,
  Banknote, ShieldCheck, ExternalLink, Download,
} from "lucide-react";

const money = (n) => `$${(n ?? 0).toFixed(2)}`;
const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

const Stat = ({ icon: Icon, label, value, tint }) => (
  <div className="rounded-xl border border-border bg-card p-4" data-testid={`aff-stat-${label.toLowerCase().replace(/\s+/g,"-")}`}>
    <div className={`h-8 w-8 rounded-lg flex items-center justify-center mb-2 ${tint}`}><Icon className="h-4 w-4" /></div>
    <div className="text-2xl font-display font-bold">{value}</div>
    <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
  </div>
);

export default function AffiliatePage() {
  const [s, setS] = useState(null);
  const [assets, setAssets] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [slug, setSlug] = useState("");
  const [savingSlug, setSavingSlug] = useState(false);
  const [payEmail, setPayEmail] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [payMethod, setPayMethod] = useState("stripe");

  const load = () => {
    api.get("/affiliate/me").then((r) => { setS(r.data); setSlug(r.data.slug || ""); setPayEmail(r.data.payout_email || ""); });
    api.get("/affiliate/assets").then((r) => setAssets(r.data));
    api.get("/affiliate/payouts").then((r) => setPayouts(r.data));
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("connect") === "done") {
      toast.success("Stripe details submitted — verifying payout status.");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("connect") === "refresh") {
      toast.message("Resume your Stripe payout setup any time.");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  if (!s) return <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const origin = window.location.origin;
  const refLink = `${origin}/ref/${s.slug || s.code}`;

  const saveSlug = async () => {
    setSavingSlug(true);
    try { const r = await api.post("/affiliate/slug", { slug }); toast.success("Custom link saved"); setS({ ...s, slug: r.data.slug }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save link"); }
    finally { setSavingSlug(false); }
  };
  const requestPayout = async () => {
    if (payMethod === "paypal" && !payEmail) { toast.error("Enter your PayPal email"); return; }
    setRequesting(true);
    try {
      const body = payMethod === "stripe" ? { method: "stripe" } : { method: "paypal", email: payEmail };
      const r = await api.post("/affiliate/payout-request", body);
      toast.success(`Payout of ${money(r.data.amount)} requested`); load();
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Payout request failed"); }
    finally { setRequesting(false); }
  };
  const startConnect = async () => {
    setConnecting(true);
    try {
      const r = await api.post("/affiliate/connect", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start Stripe setup"); setConnecting(false); }
  };
  const downloadStatement = async () => {
    try {
      const r = await api.get("/affiliate/statement.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "autogridsocial-affiliate-statement.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Statement downloaded");
    } catch (e) { toast.error("Could not download statement"); }
  };

  const connect = s.connect || {};
  const payoutsReady = !!connect.payouts_enabled;
  const canPayout = s.cleared_earnings >= s.min_payout && (payMethod === "paypal" || payoutsReady);

  return (
    <div className="space-y-8" data-testid="affiliate-page">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-primary/15 flex items-center justify-center"><Gift className="h-5 w-5 text-primary" /></div>
        <div>
          <h1 className="text-3xl font-display font-bold tracking-tight">Affiliate / Earn</h1>
          <p className="text-muted-foreground mt-1">Earn {(s.commission_rate * 100).toFixed(0)}% recurring on every paying referral.</p>
        </div>
        {s.is_vip && <Badge className="ml-auto bg-amber-500/15 text-amber-400 border-amber-500/40"><Crown className="h-3.5 w-3.5 mr-1" /> VIP {(s.commission_rate*100).toFixed(0)}%</Badge>}
      </div>

      {/* Referral link */}
      <div className="rounded-xl border border-border bg-card p-6 space-y-4" data-testid="aff-link-card">
        <div>
          <h3 className="font-semibold flex items-center gap-2 mb-2"><Link2 className="h-4 w-4 text-primary" /> Your referral link</h3>
          <div className="flex gap-2">
            <Input readOnly value={refLink} className="font-mono text-sm" data-testid="aff-link-input" />
            <Button onClick={() => copy(refLink)} data-testid="aff-copy-link" className="bg-primary hover:bg-primary/90 text-white shrink-0"><Copy className="h-4 w-4" /></Button>
          </div>
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-1.5">Custom link name (optional)</p>
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-sm text-muted-foreground font-mono break-all">{origin}/ref/</span>
            <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="yourname" className="max-w-[220px]" data-testid="aff-slug-input" />
            <Button variant="outline" onClick={saveSlug} disabled={savingSlug} data-testid="aff-save-slug" className="border-primary/40 text-primary">
              {savingSlug ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={MousePointerClick} label="Clicks" value={s.clicks} tint="bg-sky-500/15 text-sky-400" />
        <Stat icon={UserPlus} label="Trial signups" value={s.trial_signups} tint="bg-indigo-500/15 text-indigo-400" />
        <Stat icon={CreditCard} label="Active paid subs" value={s.active_paid_subs} tint="bg-emerald-500/15 text-emerald-400" />
        <Stat icon={DollarSign} label="Lifetime paid" value={money(s.paid_earnings)} tint="bg-amber-500/15 text-amber-400" />
      </div>

      {/* Earnings + payout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-border bg-card p-6" data-testid="aff-earnings-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2"><Wallet className="h-4 w-4 text-primary" /> Earnings</h3>
            <Button variant="outline" size="sm" onClick={downloadStatement} data-testid="aff-download-statement" className="border-border h-8 text-xs">
              <Download className="h-3.5 w-3.5 mr-1.5" /> Statement (CSV)
            </Button>
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> Pending (30-day hold)</span><span className="font-mono">{money(s.pending_earnings)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-400" /> Cleared & payable</span><span className="font-mono text-emerald-400">{money(s.cleared_earnings)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">In review</span><span className="font-mono">{money(s.in_review_earnings)}</span></div>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-6 space-y-4" data-testid="aff-payout-card">
          <div>
            <h3 className="font-semibold flex items-center gap-2">
              <Banknote className="h-4 w-4 text-primary" /> Get paid
            </h3>
            <p className="text-xs text-muted-foreground mt-1">Minimum {money(s.min_payout)} cleared to withdraw.</p>
          </div>

          {/* Stripe Connect onboarding status */}
          <div className="rounded-lg border border-border bg-secondary/20 p-4" data-testid="aff-connect-card">
            {payoutsReady ? (
              <div className="flex items-center gap-2 text-sm" data-testid="aff-connect-ready">
                <ShieldCheck className="h-4 w-4 text-emerald-400" />
                <span className="text-emerald-400 font-medium">Stripe payouts active</span>
                <span className="text-muted-foreground">— you're ready to be paid automatically.</span>
              </div>
            ) : connect.connected && connect.details_submitted ? (
              <div className="space-y-2">
                <p className="text-sm text-amber-400 flex items-center gap-2"><Clock className="h-4 w-4" /> Stripe is verifying your details.</p>
                <Button variant="outline" size="sm" onClick={startConnect} disabled={connecting} data-testid="aff-connect-resume" className="border-primary/40 text-primary">
                  {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Update Stripe details <ExternalLink className="h-3.5 w-3.5 ml-1.5" /></>}
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Connect a Stripe account to receive automatic payouts — takes about 2 minutes.</p>
                <Button onClick={startConnect} disabled={connecting} data-testid="aff-connect-start" className="bg-[#635BFF] hover:bg-[#635BFF]/90 text-white">
                  {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Set up Stripe payouts <ExternalLink className="h-3.5 w-3.5 ml-1.5" /></>}
                </Button>
              </div>
            )}
          </div>

          {/* Payout method toggle */}
          <div className="flex items-center gap-1 rounded-lg border border-border bg-secondary/40 p-0.5 w-fit" data-testid="aff-method-toggle">
            <button onClick={() => setPayMethod("stripe")} data-testid="aff-method-stripe" className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${payMethod === "stripe" ? "bg-primary text-white" : "text-muted-foreground"}`}>Stripe (auto)</button>
            <button onClick={() => setPayMethod("paypal")} data-testid="aff-method-paypal" className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${payMethod === "paypal" ? "bg-primary text-white" : "text-muted-foreground"}`}>PayPal</button>
          </div>

          {payMethod === "paypal" && (
            <Input value={payEmail} onChange={(e) => setPayEmail(e.target.value)} placeholder="paypal@email.com" data-testid="aff-payout-email" />
          )}
          <Button onClick={requestPayout} disabled={!canPayout || requesting} data-testid="aff-request-payout" className="w-full bg-primary hover:bg-primary/90 text-white">
            {requesting ? <Loader2 className="h-4 w-4 animate-spin" /> : `Request payout${payMethod === "stripe" ? " via Stripe" : ""}`}
          </Button>
          {s.cleared_earnings < s.min_payout && <p className="text-[11px] text-muted-foreground">You have {money(s.cleared_earnings)} cleared — reach {money(s.min_payout)} to withdraw.</p>}
          {s.cleared_earnings >= s.min_payout && payMethod === "stripe" && !payoutsReady && <p className="text-[11px] text-amber-400">Finish Stripe setup above to request a payout.</p>}
        </div>
      </div>

      {/* Payout history */}
      <div className="rounded-xl border border-border bg-card p-6" data-testid="aff-payout-history">
        <h3 className="font-semibold mb-4">Payout history</h3>
        {payouts.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">No payouts yet.</p> : (
          <div className="space-y-2">
            {payouts.map((p) => (
              <div key={p.id} className="flex items-center justify-between rounded-lg border border-border bg-secondary/20 px-4 py-2.5 text-sm" data-testid={`aff-payout-${p.id}`}>
                <span className="font-mono">{new Date(p.created_at).toLocaleDateString()}</span>
                <span className="font-mono">{money(p.amount)}</span>
                <Badge className={p.status === "paid" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/40" : p.status === "denied" ? "bg-rose-500/15 text-rose-400 border-rose-500/40" : "bg-amber-500/15 text-amber-400 border-amber-500/40"}>{p.status}</Badge>
                <span className="font-mono text-xs text-muted-foreground">{p.txn_id || "—"}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Asset kit */}
      {assets && (
        <div className="rounded-xl border border-border bg-card p-6" data-testid="aff-assets">
          <h3 className="font-semibold flex items-center gap-2 mb-4"><ImageIcon className="h-4 w-4 text-primary" /> Promo kit</h3>
          <div className="flex items-center gap-3 mb-4">
            <img src={assets.logo_badge_url} alt="AutoGridSocial badge" className="h-12 w-12 rounded-lg border border-border" />
            <Button variant="outline" size="sm" className="border-border" onClick={() => copy(origin + assets.logo_badge_url)}><Copy className="h-3.5 w-3.5 mr-1.5" /> Copy badge URL</Button>
          </div>
          <div className="space-y-2">
            {assets.copy_snippets.map((c, i) => (
              <div key={i} className="rounded-lg border border-border bg-secondary/20 p-3" data-testid={`aff-snippet-${i}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <Badge variant="outline" className="text-[10px] border-border"><FileText className="h-3 w-3 mr-1" /> {c.platform}</Badge>
                  <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => copy(c.text.replace("/ref/", origin + "/ref/"))}><Copy className="h-3 w-3 mr-1" /> Copy</Button>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
